// Juni 2026 — FL-Dashboard-Widget: Angebote-Hit-Rate (Annahme- vs. Ablehnungs-Quote).
// Zeigt die Erfolgsquote angenommener Angebote sowie die häufigsten Ablehnungsgründe.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Target, TrendingUp, ThumbsDown, ChevronRight } from 'lucide-react';

const fmtEur = (n) =>
  Number(n || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

export default function QuoteHitRateWidget({ year }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/quotes/stats${year ? `?year=${year}` : ''}`);
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [year]);

  // Kein Widget, solange keine Angebote existieren oder noch lädt
  if (loading || !data || data.total === 0) return null;

  const { hit_rate, accepted, declined, open, expired, decided, accepted_value, decline_reasons } = data;
  const total = accepted + declined + open + expired;
  const pct = (v) => (total > 0 ? (v / total) * 100 : 0);

  // Farbe der Hit-Rate je nach Erfolg
  const rateColor = hit_rate >= 60 ? 'text-emerald-600' : hit_rate >= 35 ? 'text-amber-600' : 'text-rose-600';

  return (
    <Card data-testid="quote-hit-rate-widget">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-violet-600" />
            <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Angebote-Hit-Rate
            </h3>
          </div>
          <button
            type="button"
            onClick={() => navigate('/quotes')}
            className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-0.5 transition-colors"
            data-testid="quote-hit-rate-goto-quotes"
          >
            Angebote <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-5 items-center">
          {/* Große Hit-Rate-Zahl */}
          <div className="text-center sm:text-left">
            <div className={`text-4xl font-bold font-mono tracking-tight ${rateColor}`} data-testid="quote-hit-rate-value">
              {decided > 0 ? `${hit_rate}%` : '—'}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {decided > 0
                ? `${accepted} von ${decided} entschieden`
                : 'Noch keine Entscheidung'}
            </p>
            {accepted_value > 0 && (
              <p className="text-xs text-emerald-700 font-medium mt-1" data-testid="quote-hit-rate-accepted-value">
                {fmtEur(accepted_value)} gewonnen
              </p>
            )}
          </div>

          {/* Status-Verteilung als Balken */}
          <div className="space-y-2.5">
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100" data-testid="quote-hit-rate-bar">
              {accepted > 0 && <div className="bg-emerald-500" style={{ width: `${pct(accepted)}%` }} title={`Angenommen: ${accepted}`} />}
              {declined > 0 && <div className="bg-rose-400" style={{ width: `${pct(declined)}%` }} title={`Abgelehnt: ${declined}`} />}
              {open > 0 && <div className="bg-blue-300" style={{ width: `${pct(open)}%` }} title={`Offen: ${open}`} />}
              {expired > 0 && <div className="bg-slate-300" style={{ width: `${pct(expired)}%` }} title={`Abgelaufen: ${expired}`} />}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <LegendDot color="bg-emerald-500" label="Angenommen" value={accepted} />
              <LegendDot color="bg-rose-400" label="Abgelehnt" value={declined} />
              <LegendDot color="bg-blue-300" label="Offen" value={open} />
              {expired > 0 && <LegendDot color="bg-slate-300" label="Abgelaufen" value={expired} />}
            </div>
          </div>
        </div>

        {/* Ablehnungsgründe */}
        {decline_reasons && decline_reasons.length > 0 && (
          <div className="mt-5 pt-4 border-t border-dashed border-slate-200" data-testid="quote-decline-reasons">
            <div className="flex items-center gap-1.5 mb-2.5">
              <ThumbsDown className="h-3.5 w-3.5 text-rose-500" />
              <span className="text-xs font-semibold text-slate-700">Häufigste Ablehnungsgründe</span>
            </div>
            <div className="space-y-1.5">
              {decline_reasons.slice(0, 4).map((r, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 text-xs"
                  data-testid={`quote-decline-reason-${i}`}
                >
                  <span className="text-slate-700 truncate flex-1" title={r.reason}>{r.reason}</span>
                  <div className="flex items-center gap-2 shrink-0 text-slate-500">
                    <span className="font-medium text-slate-700">{r.count}×</span>
                    {r.value > 0 && <span className="tabular-nums">{fmtEur(r.value)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {decided === 0 && open > 0 && (
          <p className="mt-4 text-xs text-slate-400 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />
            {open} {open === 1 ? 'Angebot wartet' : 'Angebote warten'} noch auf eine Rückmeldung.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function LegendDot({ color, label, value }) {
  return (
    <span className="flex items-center gap-1.5 text-slate-600">
      <span className={`h-2 w-2 rounded-full ${color}`} />
      {label} <span className="font-medium text-slate-800">{value}</span>
    </span>
  );
}
